import { useEffect } from 'react'
import { create } from 'zustand'

type DirtyReloadGuardState = {
  dirtyIds: Set<string>
  registerDirty: (id: string) => void
  unregisterDirty: (id: string) => void
}

const useDirtyReloadGuardStore = create<DirtyReloadGuardState>((set) => ({
  dirtyIds: new Set(),
  registerDirty: (id) => set((state) => ({ dirtyIds: new Set(state.dirtyIds).add(id) })),
  unregisterDirty: (id) =>
    set((state) => {
      const dirtyIds = new Set(state.dirtyIds)
      dirtyIds.delete(id)
      return { dirtyIds }
    }),
}))

export const isReloadBlockedByDirtyState = (): boolean =>
  useDirtyReloadGuardStore.getState().dirtyIds.size > 0

/**
 * Opt-in guard a form can use to block the stale-client silent reload (Tier 1) while it
 * has unsaved changes — pass a stable `id` (e.g. the form/modal name) and its current
 * `formState.isDirty`.
 *
 * Not wired into any existing form yet by design (EL-13298) — only `.tsx` forms being
 * newly built/refactored should opt in; existing forms are reviewed and added separately.
 */
export const useBlockReloadWhenDirty = (id: string, isDirty: boolean) => {
  const registerDirty = useDirtyReloadGuardStore((state) => state.registerDirty)
  const unregisterDirty = useDirtyReloadGuardStore((state) => state.unregisterDirty)

  useEffect(() => {
    if (isDirty) {
      registerDirty(id)
    } else {
      unregisterDirty(id)
    }

    return () => unregisterDirty(id)
  }, [id, isDirty, registerDirty, unregisterDirty])
}
